import { LinearProgress } from '@mui/material';
import { FlowKind, FlowVersionTarget } from 'flow-schema';
import { lowerCase, startCase } from 'lodash';
import { type PropsWithChildren } from 'react';

import { Alert, isDefined } from 'common';

import { FlowAutocomplete } from './Autocomplete/Flow.tsx';
import { FlowDefinitionAutocomplete } from './Autocomplete/FlowDefinition.tsx';
import { SubFlowContent, VISIBLE_SUBFLOW_FLOW_KINDS } from './Content.tsx';
import { SubFlowFieldsProvider, useSubFlowFieldsContext } from './context.ts';
import { FLOW_KIND_CONSTANTS } from '../../../../../constants.ts';
import { BlockRadioGroupField } from '../../../ConfigFields/RadioGroupField.tsx';
import { type SharedBlockFormFieldsProps } from '../../constants.tsx';

function FlowVersionFormFieldsInner(props: PropsWithChildren) {
  const { children } = props;
  const {
    fieldNamePrefix,
    variant,
    flowDefinitionResult,
    versionTargetFieldProps,
    selectedSubflow,
  } = useSubFlowFieldsContext();
  const { data: flowDefinition, isLoading, isError, error } = flowDefinitionResult;
  const { value: versionTarget } = versionTargetFieldProps;

  const flowKindContent = FLOW_KIND_CONSTANTS[selectedSubflow?.kind ?? FlowKind.Base];
  return (
    <>
      {variant === 'SubflowPicker' && <FlowAutocomplete />}
      <BlockRadioGroupField
        label="Version Target"
        fieldName={`${fieldNamePrefix}.versionTarget`}
        required
        options={Object.values(FlowVersionTarget).map((entry) => {
          return {
            label: startCase(entry),
            value: entry,
          };
        })}
        helperText="Choose the version selection strategy for the subflow version"
        row
      />
      {(versionTarget === FlowVersionTarget.SpecificVersion || versionTarget === undefined) && (
        // Select the specific flow definition
        // Also render this when loading an old version of the Subflow block that pre-dates version target
        <FlowDefinitionAutocomplete
          label="Flow Version"
          helperText="Select a specific version of the flow"
        />
      )}

      {isLoading && <LinearProgress />}
      {isError && (
        <Alert
          title={`Failed to load ${lowerCase(flowKindContent.label)}`}
          variant="warning"
          error={error}
        />
      )}
      {versionTarget === FlowVersionTarget.Published && !isDefined(flowDefinition) && (
        <Alert title={`Failed to load ${lowerCase(flowKindContent.label)}`} variant="warning">
          The {lowerCase(flowKindContent.label)} does not yet have a published version
        </Alert>
      )}

      {children}

      {isDefined(flowDefinition) && VISIBLE_SUBFLOW_FLOW_KINDS.has(flowDefinition.kind) && (
        <SubFlowContent flowDefinition={flowDefinition} />
      )}
    </>
  );
}
type FlowVersionFormFieldsProps = SharedBlockFormFieldsProps & PropsWithChildren;

export function FlowVersionFormFields(props: FlowVersionFormFieldsProps) {
  const { fieldNamePrefix, allowedFlowKinds, variant, children } = props;
  return (
    <SubFlowFieldsProvider
      fieldNamePrefix={fieldNamePrefix}
      variant={variant}
      allowedFlowKinds={allowedFlowKinds}
    >
      <FlowVersionFormFieldsInner>{children}</FlowVersionFormFieldsInner>
    </SubFlowFieldsProvider>
  );
}
